import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Card,
  Checkbox,
  Space,
  Select,
  Spin,
  Alert,
  Tag,
  Statistic,
  Row,
  Col,
  Button,
  Modal,
  Table,
  Tooltip,
  message,
  Divider,
  DatePicker
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { logger } from '@/shared/lib/logger';
import {
  DatabaseOutlined,
  SwapOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  MinusOutlined,
  SettingOutlined,
  HistoryOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useResponsive } from '@/shared/hooks/useResponsive';
import { THEME_COLORS } from '@/shared/constants/colors';
import {
  getAllInventories,
  getInventoryBySiteId,
  recordInventoryAdjustment,
  recordConsumption,
  updateThresholds,
  getTransactionHistory
} from '@/features/inventory/services/inventoryApiService';
import { InventoryAdjustmentModal } from '@/features/inventory/components/InventoryAdjustmentModal';
import {
  InventoryConsumptionModal,
  type InventoryConsumptionFormValues,
} from '@/features/inventory/components/InventoryConsumptionModal';
import {
  InventoryThresholdModal,
  type InventoryThresholdFormValues,
} from '@/features/inventory/components/InventoryThresholdModal';
import {
  type SiteInventoryData,
  type InventoryItem,
  type TransactionHistoryItem,
  type AdjustmentReasonCode,
  INVENTORY_STATUS,
  isIncreaseAdjustmentReason,
  isDecreaseAdjustmentReason,
} from '@/features/inventory/types/inventory';
import { formatDateTimeShort } from '@/shared/utils/dateFormatter';
import { TRANSACTION_HISTORY_COLUMNS } from '@/features/inventory/components/transactionHistoryColumns';
import { useAuthStore } from '@/features/auth/stores/authStore';
import {
  canEditInventory,
  canEditInventoryThreshold,
  canViewPurchaseOrderManagementLink,
} from '@/features/auth/utils/permissionUtils';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const PAGE_CONSTANTS = {
  DEFAULT_PAGE_SIZE: 10,
  QUANTITY_UNIT_LABEL: '単位'
} as const;


/**
 * 「次回から在庫調整確認をスキップ」フラグ用の localStorage キー
 *
 * 設計書 §6.4 v1.2 P1-9: 慣れたユーザーが Modal.confirm をスキップできるようにする。
 * v1.6 で「在庫調整(増)」を「在庫調整(増減統合)」に再編した際、ダイアログ文言が
 * 仕様変更を含むためキー名を `_v2` にバンプし、既存の skip 設定を無効化して
 * 新ダイアログを 1 度は必ず表示する(操作内容の意図変更見落としを防止)。
 */
const SKIP_ADJUSTMENT_CONFIRM_STORAGE_KEY = 'skipAdjustmentConfirm_v2';

const SKIP_ADJUSTMENT_CONFIRM_TRUE_VALUE = 'true';

/**
 * 着荷予定日経過の発注一覧へのリンク URL を構築
 *
 * URL クエリ生成は `URLSearchParams` で実施(getTransactionHistory と同パターン)。
 */
function buildOverdueOrdersLinkPath(params: {
  readonly materialTypeId: number;
}): string {
  const searchParams = new URLSearchParams();
  searchParams.append('materialTypeId', String(params.materialTypeId));
  return `/purchase-orders?${searchParams.toString()}`;
}

// ========== ステータス取得関数 ==========

type InventoryStatusValue = typeof INVENTORY_STATUS[keyof typeof INVENTORY_STATUS];

const getInventoryStatus = (item: InventoryItem): InventoryStatusValue => {
  if (item.isLowStock) {
    return INVENTORY_STATUS.LOW_STOCK;
  }
  if (item.isReorderRequired) {
    return INVENTORY_STATUS.REORDER_REQUIRED;
  }
  return INVENTORY_STATUS.NORMAL;
};

const getStatusIcon = (status: InventoryStatusValue): React.ReactElement => {
  switch (status.key) {
    case 'lowStock':
      return <ExclamationCircleOutlined />;
    case 'reorderRequired':
      return <WarningOutlined />;
    default:
      return <CheckCircleOutlined />;
  }
};

// ========== メインコンポーネント ==========

export const InventoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { user } = useAuthStore();
  const hasEditPermission = canEditInventory(user);
  const hasThresholdEditPermission = canEditInventoryThreshold(user);
  const showsPurchaseOrderLink = canViewPurchaseOrderManagementLink(user);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [accessibleSiteInventories, setAccessibleSiteInventories] = useState<readonly SiteInventoryData[]>([]);
  const [inventoryData, setInventoryData] = useState<SiteInventoryData | null>(null);
  const [isLoadingSites, setIsLoadingSites] = useState<boolean>(true);
  const [isLoadingInventory, setIsLoadingInventory] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // モーダル状態
  const [inventoryAdjustmentModalVisible, setInboundModalVisible] = useState<boolean>(false);
  const [consumeModalVisible, setConsumeModalVisible] = useState<boolean>(false);
  const [thresholdModalVisible, setThresholdModalVisible] = useState<boolean>(false);
  const [historyModalVisible, setHistoryModalVisible] = useState<boolean>(false);
  const [selectedMaterialTypeId, setSelectedMaterialTypeId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // 履歴データ
  const [historyData, setHistoryData] = useState<readonly TransactionHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [historyPagination, setHistoryPagination] = useState({
    current: 1,
    pageSize: PAGE_CONSTANTS.DEFAULT_PAGE_SIZE,
    total: 0
  });

  // フォーム
  const [thresholdInitialValues, setThresholdInitialValues] =
    useState<InventoryThresholdFormValues | null>(null);

  // 在庫データ読み込み
  const loadInventoryData = useCallback(async () => {
    if (selectedSiteId === null) {
      return;
    }

    try {
      setIsLoadingInventory(true);
      setError(null);
      const data = await getInventoryBySiteId({ siteId: selectedSiteId });
      setInventoryData(data);
    } catch (err) {
      setError('在庫データの取得に失敗しました');
      logger.captureError(err, 'InventoryPage.loadInventory');
    } finally {
      setIsLoadingInventory(false);
    }
  }, [selectedSiteId]);

  // 現場リスト読み込み（クリーンアップ対応）
  // 在庫は現場単位で管理するため、選択肢はアクセス可能な現場(サーバー側でロールに応じて絞り込み済み)
  useEffect(() => {
    let isMounted = true;

    const loadAvailableSites = async (): Promise<void> => {
      try {
        setIsLoadingSites(true);
        const sites = await getAllInventories();

        if (isMounted) {
          setAccessibleSiteInventories(sites);
          setSelectedSiteId(sites[0]?.siteId ?? null);
        }
      } catch (err) {
        if (isMounted) {
          setError('現場情報の取得に失敗しました');
          logger.captureError(err, 'InventoryPage.loadAvailableSites');
        }
      } finally {
        if (isMounted) {
          setIsLoadingSites(false);
        }
      }
    };

    loadAvailableSites();

    return () => {
      isMounted = false;
    };
  }, []);

  // 在庫データ読み込み
  useEffect(() => {
    loadInventoryData();
  }, [loadInventoryData]);

  // 現場選択変更
  const handleSiteChange = (newSiteId: number): void => {
    setSelectedSiteId(newSiteId);
  };

  // 在庫調整モーダルを直接開く
  const openInventoryAdjustmentModal = (materialTypeId: number): void => {
    setSelectedMaterialTypeId(materialTypeId);
    setInboundModalVisible(true);
  };

  /**
   * 在庫調整ボタンクリック時の確認ダイアログ(設計書 §6.4 v1.2 P1-9、v1.6 増減統合)
   *
   * `localStorage` で「次回から表示しない」を保持。確認 OK でモーダルを開く。
   */
  const handleInventoryAdjustmentButtonClick = useCallback(
    (materialTypeId: number): void => {
      const skipConfirm =
        localStorage.getItem(SKIP_ADJUSTMENT_CONFIRM_STORAGE_KEY) ===
        SKIP_ADJUSTMENT_CONFIRM_TRUE_VALUE;
      if (skipConfirm) {
        openInventoryAdjustmentModal(materialTypeId);
        return;
      }
      Modal.confirm({
        title: '在庫調整の確認',
        content: (
          <div>
            <p>これは「在庫調整(増減を含む特殊な入出庫)」の操作です。</p>
            <p>
              <strong>新規購入による入庫は『発注管理』から実施してください。</strong>
            </p>
            <Checkbox
              onChange={(event) => {
                if (event.target.checked) {
                  localStorage.setItem(
                    SKIP_ADJUSTMENT_CONFIRM_STORAGE_KEY,
                    SKIP_ADJUSTMENT_CONFIRM_TRUE_VALUE
                  );
                } else {
                  localStorage.removeItem(SKIP_ADJUSTMENT_CONFIRM_STORAGE_KEY);
                }
              }}
            >
              次回から表示しない
            </Checkbox>
          </div>
        ),
        okText: '在庫調整を続ける',
        cancelText: 'キャンセル',
        onOk: () => openInventoryAdjustmentModal(materialTypeId),
      });
    },
    []
  );

  // 消費モーダル開く
  const openConsumeModal = (materialTypeId: number): void => {
    setSelectedMaterialTypeId(materialTypeId);
    setConsumeModalVisible(true);
  };

  // 閾値設定モーダル開く
  const openThresholdModal = (item: InventoryItem): void => {
    setSelectedMaterialTypeId(item.materialTypeId);
    setThresholdInitialValues({
      minimumThreshold: item.minimumThreshold,
      reorderThreshold: item.reorderThreshold
    });
    setThresholdModalVisible(true);
  };

  // 履歴モーダル開く
  const openHistoryModal = async (materialTypeId?: number): Promise<void> => {
    setSelectedMaterialTypeId(materialTypeId ?? null);
    setHistoryModalVisible(true);
    await loadHistory(1, materialTypeId);
  };

  // 履歴読み込み
  const loadHistory = async (
    page: number,
    materialTypeId?: number,
    dateRange?: [dayjs.Dayjs, dayjs.Dayjs] | null
  ): Promise<void> => {
    if (selectedSiteId === null) {
      return;
    }

    try {
      setHistoryLoading(true);
      const result = await getTransactionHistory({
        siteId: selectedSiteId,
        query: {
          materialTypeId,
          page,
          limit: PAGE_CONSTANTS.DEFAULT_PAGE_SIZE,
          startDate: dateRange?.[0]?.startOf('day').toISOString(),
          endDate: dateRange?.[1]?.endOf('day').toISOString()
        }
      });
      setHistoryData(result.transactions);
      setHistoryPagination({
        current: result.pagination.currentPage,
        pageSize: PAGE_CONSTANTS.DEFAULT_PAGE_SIZE,
        total: result.pagination.totalCount
      });
    } catch (err) {
      message.error('操作履歴の取得に失敗しました');
      logger.captureError(err, 'InventoryPage.loadTransactionHistory');
    } finally {
      setHistoryLoading(false);
    }
  };

  /**
   * 在庫調整処理(増減統合)
   *
   * v1.6 / v1.6.1: reasonCode の方向で API を振り分ける。
   * - 増側 (`ADJUSTMENT_IN` / `OTHER_IN`): `recordInventoryAdjustment`
   * - 減側 (`ADJUSTMENT_OUT` / `OTHER_OUT`): `recordConsumption`(サーバー API は据え置き)
   */
  const handleInventoryAdjustmentSubmit = async (values: {
    quantity: number;
    reasonCode: AdjustmentReasonCode;
    note?: string;
  }): Promise<void> => {
    if (selectedSiteId === null || selectedMaterialTypeId === null) {
      return;
    }

    try {
      setIsSubmitting(true);
      if (isIncreaseAdjustmentReason(values)) {
        await recordInventoryAdjustment({
          siteId: selectedSiteId,
          request: {
            materialTypeId: selectedMaterialTypeId,
            quantity: values.quantity,
            reasonCode: values.reasonCode,
            note: values.note,
          },
        });
      } else if (isDecreaseAdjustmentReason(values)) {
        await recordConsumption({
          siteId: selectedSiteId,
          request: {
            materialTypeId: selectedMaterialTypeId,
            quantity: values.quantity,
            reasonCode: values.reasonCode,
            note: values.note,
          },
        });
      } else {
        // 防御的フォールバック: AdjustmentReasonCode に新値が追加されて
        // 既存の型ガード 2 種に該当しなくなった場合、API 未呼出で成功通知が
        // 出る silent success を防ぐためランタイムで例外化する。
        // (generic 型ガードの否定形は TS が narrowing しきれないため
        //  `const x: never` 形式の静的 exhaustive check は使わない)
        throw new Error(
          `未対応の在庫調整理由コード: ${String(values.reasonCode)}`
        );
      }
      message.success('在庫調整が完了しました');
      setInboundModalVisible(false);
      await loadInventoryData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '在庫調整に失敗しました';
      message.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 消費処理
  const handleConsume = async (values: InventoryConsumptionFormValues): Promise<void> => {
    if (selectedSiteId === null || selectedMaterialTypeId === null) {
      return;
    }

    try {
      setIsSubmitting(true);
      await recordConsumption({
        siteId: selectedSiteId,
        request: {
          materialTypeId: selectedMaterialTypeId,
          quantity: values.quantity,
          reasonCode: values.reasonCode,
          note: values.note
        }
      });
      message.success('消費処理が完了しました');
      setConsumeModalVisible(false);
      await loadInventoryData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '消費処理に失敗しました';
      message.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 閾値設定更新
  const handleUpdateThresholds = async (values: InventoryThresholdFormValues): Promise<void> => {
    if (selectedSiteId === null || selectedMaterialTypeId === null) {
      return;
    }

    try {
      setIsSubmitting(true);
      await updateThresholds({
        siteId: selectedSiteId,
        request: {
          materialTypeId: selectedMaterialTypeId,
          minimumThreshold: values.minimumThreshold,
          reorderThreshold: values.reorderThreshold
        }
      });
      message.success('閾値設定を更新しました');
      setThresholdModalVisible(false);
      await loadInventoryData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '閾値設定の更新に失敗しました';
      message.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ローディング表示
  if (isLoadingSites) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Spin size="large" />
      </div>
    );
  }

  // 選択中の材料名を取得
  const getSelectedMaterialName = (): string => {
    if (selectedMaterialTypeId === null || inventoryData === null) {
      return '';
    }
    const item = inventoryData.inventories.find(
      (inv) => inv.materialTypeId === selectedMaterialTypeId
    );
    return item?.materialTypeName ?? '';
  };

  return (
    <div>
      <div className="mb-4">
        <Space
          size={isMobile ? 'middle' : 'large'}
          direction={isMobile ? 'vertical' : 'horizontal'}
          className={isMobile ? 'w-full' : ''}
        >
          <Select
            value={selectedSiteId ?? undefined}
            onChange={handleSiteChange}
            className={isMobile ? 'w-full' : 'w-[300px]'}
            suffixIcon={<SwapOutlined />}
            size="large"
            placeholder="現場を選択してください"
          >
            {accessibleSiteInventories.map((site) => (
              <Select.Option key={site.siteId} value={site.siteId}>
                {site.siteName}
              </Select.Option>
            ))}
          </Select>
          <Button
            icon={<HistoryOutlined />}
            onClick={() => openHistoryModal()}
            disabled={selectedSiteId === null}
          >
            操作履歴
          </Button>
          {showsPurchaseOrderLink && (
            <Button
              icon={<ShoppingCartOutlined />}
              onClick={() => navigate('/purchase-orders')}
            >
              発注管理へ
            </Button>
          )}
        </Space>
      </div>

      {error !== null && (
        <Alert
          message="エラー"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          className="mb-4"
        />
      )}

      <Space direction="vertical" size="large" className="w-full">
        {isLoadingInventory ? (
          <Card>
            <div className="flex justify-center items-center py-[60px] px-5">
              <Spin size="large" />
            </div>
          </Card>
        ) : inventoryData !== null ? (
          <Card
            title={
              <Space>
                <DatabaseOutlined />
                在庫状況
              </Space>
            }
          >
            <Row gutter={[16, 16]}>
              {inventoryData.inventories.map((item) => {
                const status = getInventoryStatus(item);
                return (
                  <Col xs={24} sm={12} key={item.materialTypeId}>
                    <Card
                      type="inner"
                      title={
                        <Space>
                          {item.materialTypeName}
                          <Tag color={status.color} icon={getStatusIcon(status)}>
                            {status.label}
                          </Tag>
                          {item.hasOverdueOrders && (
                            <Tooltip title="入庫予定日経過の発注があります">
                              <WarningOutlined
                                role="button"
                                aria-label="入庫予定日経過の発注を確認"
                                style={{ color: THEME_COLORS.WARNING, cursor: 'pointer' }}
                                onClick={() =>
                                  navigate(buildOverdueOrdersLinkPath({
                                    materialTypeId: item.materialTypeId,
                                  }))
                                }
                              />
                            </Tooltip>
                          )}
                        </Space>
                      }
                      extra={
                        hasThresholdEditPermission ? (
                          <Button
                            type="text"
                            icon={<SettingOutlined />}
                            onClick={() => openThresholdModal(item)}
                            title="閾値設定"
                          />
                        ) : null
                      }
                    >
                      <Space direction="vertical" size="middle" className="w-full">
                        <Statistic
                          title="現在在庫量"
                          value={item.currentQuantity}
                          suffix={PAGE_CONSTANTS.QUANTITY_UNIT_LABEL}
                          valueStyle={{
                            color: status.badgeColor,
                            fontSize: '28px'
                          }}
                        />
                        {item.pendingOrderedQuantity > 0 && (
                          <Text type="secondary" className="text-xs">
                            発注中: {item.pendingOrderedQuantity}
                            {PAGE_CONSTANTS.QUANTITY_UNIT_LABEL}
                          </Text>
                        )}
                        <div className="text-gray-500 text-sm">
                          <div>最低在庫閾値: {item.minimumThreshold}{PAGE_CONSTANTS.QUANTITY_UNIT_LABEL}</div>
                          <div>発注点閾値: {item.reorderThreshold}{PAGE_CONSTANTS.QUANTITY_UNIT_LABEL}</div>
                        </div>
                        {item.lastTransactionAt !== null && (
                          <Text type="secondary" className="text-xs">
                            最終操作: {formatDateTimeShort(item.lastTransactionAt)}
                            {item.lastTransactionUserName !== null && ` (${item.lastTransactionUserName})`}
                          </Text>
                        )}
                        {hasEditPermission && (
                          <>
                            <Divider className="my-2" />
                            <Space>
                              <Button
                                onClick={() =>
                                  handleInventoryAdjustmentButtonClick(item.materialTypeId)
                                }
                              >
                                在庫調整
                              </Button>
                              <Button
                                icon={<MinusOutlined />}
                                onClick={() => openConsumeModal(item.materialTypeId)}
                                disabled={item.currentQuantity === 0}
                              >
                                消費
                              </Button>
                            </Space>
                          </>
                        )}
                      </Space>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </Card>
        ) : (
          <Card>
            <div className="text-center py-[60px]">
              <Text type="secondary">現場を選択してください</Text>
            </div>
          </Card>
        )}
      </Space>

      {/* 在庫調整モーダル(増減統合、v1.6) */}
      {/* 非表示中に Form インスタンスへアクセスしないよう、表示中のみマウントする */}
      {inventoryAdjustmentModalVisible && (
        <InventoryAdjustmentModal
          open={inventoryAdjustmentModalVisible}
          title={`在庫調整 - ${getSelectedMaterialName()}`}
          isSubmitting={isSubmitting}
          onSubmit={handleInventoryAdjustmentSubmit}
          onCancel={() => setInboundModalVisible(false)}
        />
      )}

      {/* 消費モーダル */}
      {consumeModalVisible && (
        <InventoryConsumptionModal
          open={consumeModalVisible}
          title={`消費 - ${getSelectedMaterialName()}`}
          isSubmitting={isSubmitting}
          onSubmit={handleConsume}
          onCancel={() => setConsumeModalVisible(false)}
        />
      )}

      {/* 閾値設定モーダル */}
      {thresholdModalVisible && thresholdInitialValues !== null && (
        <InventoryThresholdModal
          open={thresholdModalVisible}
          title={`閾値設定 - ${getSelectedMaterialName()}`}
          isSubmitting={isSubmitting}
          initialValues={thresholdInitialValues}
          onSubmit={handleUpdateThresholds}
          onCancel={() => setThresholdModalVisible(false)}
        />
      )}

      {/* 操作履歴モーダル */}
      <Modal
        title="操作履歴"
        open={historyModalVisible}
        onCancel={() => setHistoryModalVisible(false)}
        footer={null}
        width={isMobile ? '100%' : 1000}
        destroyOnClose
      >
        <Space direction="vertical" className="w-full" size="middle">
          <Space wrap>
            <Select
              placeholder="材料で絞り込み"
              allowClear
              style={{ width: 200 }}
              value={selectedMaterialTypeId ?? undefined}
              onChange={(value) => {
                setSelectedMaterialTypeId(value ?? null);
                loadHistory(1, value ?? undefined);
              }}
            >
              {inventoryData?.inventories.map((item) => (
                <Select.Option key={item.materialTypeId} value={item.materialTypeId}>
                  {item.materialTypeName}
                </Select.Option>
              ))}
            </Select>
            <RangePicker
              onChange={(dates) => {
                loadHistory(
                  1,
                  selectedMaterialTypeId ?? undefined,
                  dates as [dayjs.Dayjs, dayjs.Dayjs] | null
                );
              }}
            />
          </Space>
          <Table
            columns={TRANSACTION_HISTORY_COLUMNS}
            dataSource={[...historyData]}
            rowKey="id"
            loading={historyLoading}
            pagination={{
              ...historyPagination,
              showSizeChanger: false,
              showTotal: (total) => `全${total}件`,
              onChange: (page) => loadHistory(page, selectedMaterialTypeId ?? undefined)
            }}
            scroll={{ x: 900 }}
            // 特記事項の ellipsis を外すと既定が auto になり列幅が変わるため、従来どおり fixed を明示する
            tableLayout="fixed"
            size="small"
          />
        </Space>
      </Modal>
    </div>
  );
};
